/**
 * 오늘의 모임 카드 — 자연어 시간 / 지역명 추출 / 레이스 멘트 선택 헬퍼.
 */

import type { TFunction } from 'i18next';

// ========================================================================
// 1. 지역명 추출 — placeAddress → 시/구 단위로 짧게
// ========================================================================

const KOREAN_PROVINCE_SHORT: Record<string, string> = {
    '서울특별시': '서울',
    '부산광역시': '부산',
    '대구광역시': '대구',
    '인천광역시': '인천',
    '광주광역시': '광주',
    '대전광역시': '대전',
    '울산광역시': '울산',
    '세종특별자치시': '세종',
    '경기도': '경기',
    '강원특별자치도': '강원',
    '강원도': '강원',
    '충청북도': '충북',
    '충청남도': '충남',
    '전라북도': '전북',
    '전북특별자치도': '전북',
    '전라남도': '전남',
    '경상북도': '경북',
    '경상남도': '경남',
    '제주특별자치도': '제주',
};

/**
 * 풀 주소에서 "시/도 + 시/군/구" 만 추출. 한국 주소 우선, 그 외엔 첫 두 토큰.
 *
 * 예:
 *  - "서울특별시 강남구 역삼동 ..." → "서울 강남구"
 *  - "경기도 성남시 분당구 ..." → "경기 성남시"
 *  - "Tokyo, Shibuya ..." → "Tokyo Shibuya"
 *  - "" → ""
 */
export const formatRegionShort = (placeAddress?: string | null): string => {
    if (!placeAddress) return '';
    const trimmed = placeAddress.trim();
    if (!trimmed) return '';

    const tokens = trimmed.split(/\s+/);
    if (tokens.length === 0) return '';

    const first = tokens[0];
    const provinceShort = KOREAN_PROVINCE_SHORT[first];

    if (provinceShort && tokens.length >= 2) {
        return `${provinceShort} ${tokens[1]}`;
    }
    if (provinceShort) {
        return provinceShort;
    }

    // 비한국 주소 — 첫 두 토큰 (콤마 제거)
    return tokens.slice(0, 2).join(' ').replace(/,/g, '');
};

// ========================================================================
// 2. 자연어 남은 시간 — "1시간 30분 남았어요" 등
// ========================================================================

export interface TimeUntilState {
    /** ko/en/ja 번역된 즉시 표시용 텍스트 */
    text: string;
    /** 강조 컬러 사용 여부 (곧 시작 / 진행 중) */
    accent: boolean;
}

/**
 * 모임 시작/종료 시각 기준 자연어 표현.
 *
 * @param startMs scheduleStartDateTime epoch ms
 * @param endMs   scheduleEndDateTime epoch ms (없으면 start+1h 가정 안 함, 종료 판정만 skip)
 * @param now     현재 시각 ms
 * @param t       i18next translation 함수 (home.timeUntil.* 키 참조)
 */
export const formatTimeUntil = (
    startMs: number | null,
    endMs: number | null,
    now: number,
    t: TFunction,
): TimeUntilState | null => {
    if (startMs == null) return null;

    const diffMs = startMs - now;
    const minutesUntil = Math.round(diffMs / 60_000);
    const minutesAfterStart = Math.round((now - startMs) / 60_000);

    // 종료된 경우
    if (endMs != null && now > endMs) {
        const minsAfterEnd = Math.round((now - endMs) / 60_000);
        if (minsAfterEnd <= 30) {
            return { text: t('home.timeUntil.justEnded'), accent: false };
        }
        const hoursAfter = Math.max(1, Math.round(minsAfterEnd / 60));
        return { text: t('home.timeUntil.ended', { h: hoursAfter }), accent: false };
    }

    // 진행 중 — 종료까지 남은 시간을 일/시간/분으로 분해 표시
    // · ≤10분: "곧 모임이 종료됩니다" 임박 표시
    // · ≤59분: "X분 남았어요"
    // · ≤24시간: "Y시간 Z분 남았어요"
    // · 그 이상 (여행 모임 등): "X일 Y시간 Z분 남았어요"
    if (endMs != null && now >= startMs && now <= endMs) {
        const minsLeft = Math.max(0, Math.round((endMs - now) / 60_000));
        if (minsLeft <= 10) {
            return {
                text: t('home.timeUntil.endingSoon'),
                accent: true,
            };
        }
        if (minsLeft < 60) {
            return { text: t('home.timeUntil.inProgress', { m: minsLeft }), accent: true };
        }
        const days = Math.floor(minsLeft / (60 * 24));
        const hours = Math.floor((minsLeft % (60 * 24)) / 60);
        const mins = minsLeft % 60;
        if (days > 0) {
            return {
                text: t('home.timeUntil.inProgressDayHourMin', { d: days, h: hours, m: mins }),
                accent: true,
            };
        }
        return {
            text: t('home.timeUntil.inProgressHourMin', { h: hours, m: mins }),
            accent: true,
        };
    }

    // 시작 직후 (종료 정보 없는 경우 +30분 까지는 "지금 진행 중")
    if (endMs == null && minutesAfterStart >= 0 && minutesAfterStart <= 30) {
        return { text: t('home.timeUntil.inProgressNoEnd', { m: minutesAfterStart }), accent: true };
    }

    // 매우 임박 (0~5분)
    if (minutesUntil >= 0 && minutesUntil <= 5) {
        return { text: t('home.timeUntil.veryClose'), accent: true };
    }

    // 5~30분
    if (minutesUntil > 5 && minutesUntil <= 30) {
        return { text: t('home.timeUntil.minOnly', { m: minutesUntil }), accent: true };
    }

    // 30~60분
    if (minutesUntil > 30 && minutesUntil <= 60) {
        return { text: t('home.timeUntil.minOnly', { m: minutesUntil }), accent: false };
    }

    // 1시간 ~ 같은 날
    const hours = Math.floor(minutesUntil / 60);
    const mins = minutesUntil % 60;

    if (hours >= 1 && hours <= 12) {
        if (mins === 0) {
            return { text: t('home.timeUntil.hourOnly', { h: hours }), accent: false };
        }
        return { text: t('home.timeUntil.hourMin', { h: hours, m: mins }), accent: false };
    }

    // 12시간 이상 — "오늘 19:00" 또는 일자
    const startDate = new Date(startMs);
    const hh = String(startDate.getHours()).padStart(2, '0');
    const mm = String(startDate.getMinutes()).padStart(2, '0');
    return {
        text: t('home.timeUntil.todayLater', { time: `${hh}:${mm}`, h: hours }),
        accent: false,
    };
};

// ========================================================================
// 3. 레이스 멘트 — 상황 × 순위 → 카탈로그에서 랜덤 하나 선택
// ========================================================================

export type RaceSituation =
    | 'preStart'      // 시작 전 (1h~출발 직전)
    | 'inProgress'    // 진행 중 — 일반 (출발 직후 ~ 시작 -5분)
    | 'lastSpurt'     // 막판 스퍼트 (시작 -5분 ~ 시작 +15분, 미확정)
    | 'finalized'     // 확정됨
    | 'ended';        // 종료 후 (결과 안 본 상태 유도)

export type RaceRankBucket =
    | 'first'    // 1등
    | 'second'   // 2등
    | 'third'    // 3등 (확정에서만 별도)
    | 'middle'   // 중간
    | 'behind'   // 후미 (꼴등 직전)
    | 'last';    // 꼴등

/**
 * 거리/순위로부터 rank bucket 결정.
 */
export const resolveRankBucket = (rank: number | null, total: number): RaceRankBucket | null => {
    if (rank == null || total <= 0) return null;
    if (rank === 1) return 'first';
    if (rank === 2) return 'second';
    if (rank === 3 && total >= 4) return 'third';
    if (rank === total) return 'last';
    if (rank === total - 1 && total >= 4) return 'behind';
    return 'middle';
};

/**
 * race 시점 결정 — 모임 시간 / finalize 여부 기반.
 */
export const resolveRaceSituation = (params: {
    raceEnabled: boolean;
    raceFinalizedAt: string | null | undefined;
    startMs: number | null;
    endMs: number | null;
    now: number;
}): RaceSituation | null => {
    const { raceEnabled, raceFinalizedAt, startMs, endMs, now } = params;
    if (!raceEnabled) return null;
    if (raceFinalizedAt) return 'finalized';

    if (startMs == null) return 'preStart';

    const minsUntil = (startMs - now) / 60_000;

    if (endMs != null && now > endMs) return 'ended';
    if (minsUntil > 10) return 'preStart';
    if (minsUntil <= 10 && minsUntil >= -15) return 'lastSpurt';
    return 'inProgress';
};

/**
 * 카탈로그에서 멘트 1개 무작위 선택. seed 로 안정화 (mount 마다 회전 OK, re-render 안에서는 같은 값).
 *
 * i18n 카탈로그는 배열 형태 — t(key, { returnObjects: true }) 로 받음.
 */
export const pickRaceMessage = (params: {
    situation: RaceSituation;
    bucket: RaceRankBucket | null;
    t: TFunction;
    seed?: number;
}): string => {
    const { situation, bucket, t, seed } = params;

    // 카탈로그 키 결정
    let key: string;
    if (situation === 'preStart') {
        key = 'home.race.preStart';
    } else if (situation === 'ended') {
        key = 'home.race.ended';
    } else if (bucket == null) {
        // 순위 정보 없음 — 일반 멘트
        key = `home.race.${situation}.unknown`;
    } else {
        key = `home.race.${situation}.${bucket}`;
    }

    const raw = t(key, { returnObjects: true, defaultValue: '' });
    let arr: string[];
    if (Array.isArray(raw)) {
        arr = raw as string[];
    } else if (typeof raw === 'string' && raw) {
        arr = [raw];
    } else {
        // fallback — 순위 무관 일반 멘트
        const generic = t(`home.race.${situation}.unknown`, { returnObjects: true, defaultValue: [] });
        arr = Array.isArray(generic) ? (generic as string[]) : [];
    }

    if (arr.length === 0) return '';
    const idx = ((seed ?? 0) >>> 0) % arr.length;
    return arr[idx];
};
